import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { cn } from "@/utils/cn";
import { Check, Lock } from "lucide-react";
import { ADVISOR_KIND_VARIANT, advisorMeta } from "../advisor.data";
import type { IAdvisor } from "../types";
import { AdvisorGlyph } from "./AdvisorGlyph";

interface AdvisorOptionProps {
  advisor: IAdvisor;
  isActive: boolean;
  onSelect: (slug: string) => void;
}

/** One row in the advisor dropdown. Presentational — the picker owns state. */
export const AdvisorOption = ({
  advisor,
  isActive,
  onSelect,
}: AdvisorOptionProps) => {
  const { t } = useTranslation(["common"]);
  const locked = !!advisor.locked;
  const meta = advisorMeta(advisor);

  return (
    <DropdownMenuItem
      disabled={locked}
      onSelect={() => !locked && onSelect(advisor.slug)}
      className={cn(
        "items-start gap-2.5 px-2 py-2",
        isActive && "bg-primary-muted focus:bg-primary-muted",
      )}
    >
      <AdvisorGlyph advisor={advisor} isActive={isActive} className="mt-0.5" />

      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-sm font-semibold text-foreground">
            {t(advisor.name)}
          </span>
          <Badge
            variant={ADVISOR_KIND_VARIANT[advisor.kind]}
            dot={false}
            className="px-1.5 py-0 text-[10px] uppercase tracking-wide"
          >
            {t(`common:ai_assistant.kind_${advisor.kind}`)}
          </Badge>
        </span>

        <span className="mt-0.5 block whitespace-normal text-xs text-muted-foreground">
          {t(advisor.tagline)}
        </span>

        <span className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          {locked ? (
            <>
              <Lock className="size-3" />
              {advisor.lockReason}
            </>
          ) : (
            t(meta.sources, { count: meta.sourcesCount })
          )}
        </span>
      </span>

      {isActive && <Check className="mt-1 size-4 shrink-0 text-primary" />}
    </DropdownMenuItem>
  );
};
